import React, { useEffect, useState } from "react";
import { PageHeader, Toast } from "@/components/Layout";
import api from "@/lib/api";

/**
 * Superadmin > Actualizări — zona de pregătire a release-urilor.
 *
 * Bazinul de mai jos se umple automat (Claude adaugă o intrare după fiecare
 * funcționalitate/fix implementat — vezi CLAUDE.md). Aici alegi ce intră
 * într-un release, dai un număr de versiune + referința git din care se
 * construiește arhiva, și alegi cui se publică (toți clienții sau o
 * selecție). Un release PUBLICAT e imuabil — nu mai poate fi editat sau
 * șters, doar create noi de-acum înainte.
 */
export default function UpdatesStaging() {
  const [entries, setEntries] = useState(null);
  const [releases, setReleases] = useState(null);
  const [clients, setClients] = useState(null);
  const [selectedEntryIds, setSelectedEntryIds] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [form, setForm] = useState({ version: "", git_ref: "", target_scope: "all", cuis: [] });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ text: "", type: "info" });
  const [expandedReleaseId, setExpandedReleaseId] = useState(null);
  const [releaseEntries, setReleaseEntries] = useState({});
  const [suggest, setSuggest] = useState(null);
  const [publishingId, setPublishingId] = useState(null);
  const [publishConfirm, setPublishConfirm] = useState("");
  // „Publică și pentru alt client": același cod, release nou (versiune mai mare).
  const [repub, setRepub] = useState(null); // { id, version, cuis, scope, confirm }

  // Precompletează versiunea (următorul minor) și commitul curent, doar în
  // câmpurile încă goale — ce a scris deja superadminul nu se suprascrie.
  const loadSuggest = () => api.get("/updates/releases/suggest").then(r => {
    setSuggest(r.data);
    setForm(f => ({
      ...f,
      version: f.version || r.data.suggested.minor,
      git_ref: f.git_ref || r.data.git_ref,
    }));
  }).catch(() => {});

  const load = () => {
    loadSuggest();
    api.get("/updates/changelog-entries").then(r => setEntries(r.data));
    api.get("/updates/releases").then(r => setReleases(r.data));
    api.get("/clients").then(r => setClients(r.data));
  };
  useEffect(() => { load(); }, []);

  const flash = (text, type) => { setMsg({ text, type }); if (type === "success") setTimeout(() => setMsg({ text: "" }), 3000); };

  const startRepub = (r) => {
    const base = (suggest?.suggested?.patch) || "";
    setRepub({ id: r.id, source: r.version, version: base, cuis: [], scope: "selected", confirm: "" });
  };
  const doRepub = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/updates/releases/${repub.id}/republish`, {
        version: repub.version.trim(), target_scope: repub.scope, cuis: repub.cuis,
        confirm_version: repub.confirm.trim(),
      });
      flash(`Publicat v${res.data.version}`, "success");
      setRepub(null); load();
    } catch (e) { flash(e?.response?.data?.detail || "Eroare la publicare", "error"); }
    finally { setBusy(false); }
  };

  const toggleEntry = (id) => setSelectedEntryIds(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const saveEdit = async (id) => {
    try {
      await api.put(`/updates/changelog-entries/${id}`, { description: editText });
      setEditingId(null); load();
    } catch (e) { flash(e?.response?.data?.detail || "Eroare la salvare", "error"); }
  };

  const deleteEntry = async (id) => {
    try {
      await api.delete(`/updates/changelog-entries/${id}`);
      setSelectedEntryIds(s => s.filter(x => x !== id));
      load();
    } catch (e) { flash(e?.response?.data?.detail || "Eroare la ștergere", "error"); }
  };

  const toggleCui = (cui) => setForm(f => ({
    ...f, cuis: f.cuis.includes(cui) ? f.cuis.filter(c => c !== cui) : [...f.cuis, cui],
  }));

  const createRelease = async () => {
    if (!form.version.trim() || !form.git_ref.trim()) return flash("Versiunea și referința git sunt obligatorii", "error");
    if (!selectedEntryIds.length) return flash("Selectează cel puțin o intrare din bazin", "error");
    if (form.target_scope === "selected" && !form.cuis.length) return flash("Selectează cel puțin un client", "error");
    setBusy(true);
    try {
      await api.post("/updates/releases", { ...form, entry_ids: selectedEntryIds });
      setForm({ version: "", git_ref: "", target_scope: "all", cuis: [] });
      setSelectedEntryIds([]);
      flash("Release creat ca draft", "success");
      load();
    } catch (e) { flash(e?.response?.data?.detail || "Eroare la creare", "error"); }
    finally { setBusy(false); }
  };

  const publishRelease = async (r) => {
    if (publishConfirm.trim() !== r.version) return flash(`Tastează exact versiunea ${r.version} pentru a confirma`, "error");
    setBusy(true);
    try {
      const res = await api.post(`/updates/releases/${r.id}/publish`, { confirm_version: publishConfirm.trim() });
      const t = res.data.tag || {};
      if (t.pushed) flash(`Release publicat · tag ${t.tag} creat și împins pe GitHub`, "success");
      else flash(`Release publicat, dar tag-ul ${t.tag} NU a ajuns pe GitHub: ${t.error || "motiv necunoscut"}`, "error");
      setPublishingId(null); setPublishConfirm("");
      load();
    } catch (e) { flash(e?.response?.data?.detail || "Eroare la publicare", "error"); }
    finally { setBusy(false); }
  };

  const deleteRelease = async (id) => {
    if (!window.confirm("Ștergi acest draft? Intrările revin în bazinul nepublicat.")) return;
    try {
      await api.delete(`/updates/releases/${id}`);
      flash("Draft șters", "success");
      load();
    } catch (e) { flash(e?.response?.data?.detail || "Eroare la ștergere", "error"); }
  };

  const toggleExpand = async (id) => {
    if (expandedReleaseId === id) { setExpandedReleaseId(null); return; }
    setExpandedReleaseId(id);
    if (!releaseEntries[id]) {
      const r = await api.get(`/updates/releases/${id}`);
      setReleaseEntries(prev => ({ ...prev, [id]: r.data.entries }));
    }
  };

  if (!entries || !releases || !clients) return <div>Se încarcă...</div>;

  return (
    <>
      <PageHeader title="Actualizări" subtitle="Zona de pregătire a release-urilor pentru clienți" />
      <Toast msg={msg.text} type={msg.type} />

      <div className="card" style={{ padding: 22, marginBottom: 16 }} data-testid="changelog-pool">
        <div style={{ fontWeight: 700, color: "var(--navy)", marginBottom: 4 }}>Bazin nepublicat ({entries.length})</div>
        <div style={{ fontSize: 12, color: "#64748B", marginBottom: 12 }}>
          Bifează ce intră în release-ul de mai jos. O intrare editată/ștearsă aici nu mai afectează nimic deja publicat.
        </div>
        {entries.length === 0 && <div style={{ color: "#94A3B8", fontSize: 13 }}>Nimic în bazin momentan.</div>}
        {entries.map(e => (
          <div key={e.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 0", borderBottom: "1px solid #E2E8F0" }} data-testid={`changelog-entry-${e.id}`}>
            <input type="checkbox" checked={selectedEntryIds.includes(e.id)} onChange={() => toggleEntry(e.id)} style={{ marginTop: 4 }} />
            <div style={{ flex: 1 }}>
              {editingId === e.id ? (
                <textarea className="input" value={editText} onChange={ev => setEditText(ev.target.value)} rows={2} style={{ width: "100%" }} />
              ) : (
                <div style={{ fontSize: 13 }}>{e.description}</div>
              )}
              <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2 }}>
                {e.category || "fără categorie"} · {(e.created_at || "").slice(0, 10)}
              </div>
            </div>
            {editingId === e.id ? (
              <button className="btn btn-secondary" onClick={() => saveEdit(e.id)}>Salvează</button>
            ) : (
              <>
                <button className="btn btn-secondary" onClick={() => { setEditingId(e.id); setEditText(e.description); }}>Editează</button>
                <button className="btn btn-secondary" onClick={() => deleteEntry(e.id)} style={{ color: "#B91C1C" }}>Șterge</button>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="card" style={{ padding: 22, marginBottom: 16 }} data-testid="release-composer">
        <div style={{ fontWeight: 700, color: "var(--navy)", marginBottom: 12 }}>
          Creează release din {selectedEntryIds.length} intrare(i) selectată(e)
        </div>
        <div className="grid-2" style={{ gap: 12 }}>
          <div><label className="label label-req">Versiune</label>
            <input className="input" placeholder="ex: 2.3.0" value={form.version}
              onChange={e => setForm({ ...form, version: e.target.value })} data-testid="release-version" />
            {suggest && (
              <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>
                Ultima versiune: <b>{suggest.latest_version}</b>{!suggest.has_releases && " (din fișierul VERSION, niciun release încă)"}.{" "}
                {[["patch", "Patch"], ["minor", "Minor"], ["major", "Major"]].map(([k, label]) => (
                  <button key={k} type="button" className="btn btn-secondary" style={{ padding: "1px 8px", fontSize: 11, marginRight: 4 }}
                    onClick={() => setForm({ ...form, version: suggest.suggested[k] })}>
                    {label} → {suggest.suggested[k]}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div><label className="label label-req">Referință git (commit, tag sau branch)</label>
            <input className="input" placeholder="ex: v2.3.0" value={form.git_ref}
              onChange={e => setForm({ ...form, git_ref: e.target.value })} data-testid="release-git-ref" />
            {suggest && suggest.git_ref && (
              <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>
                {form.git_ref === suggest.git_ref
                  ? <>Commitul curent ({suggest.git_branch}): <i>{suggest.git_subject}</i></>
                  : <button type="button" className="btn btn-secondary" style={{ padding: "1px 8px", fontSize: 11 }}
                      onClick={() => setForm({ ...form, git_ref: suggest.git_ref })}>Folosește commitul curent ({suggest.git_ref.slice(0, 7)})</button>}
                {suggest.git_dirty && <div style={{ color: "#B45309" }}>Atenție: există modificări necomise — nu intră în release, doar commiturile.</div>}
              </div>
            )}
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <label className="label">Publică pentru</label>
          <div style={{ display: "flex", gap: 16, marginTop: 4 }}>
            <label style={{ fontSize: 13 }}>
              <input type="radio" checked={form.target_scope === "all"} onChange={() => setForm({ ...form, target_scope: "all" })} /> Toți clienții
            </label>
            <label style={{ fontSize: 13 }}>
              <input type="radio" checked={form.target_scope === "selected"} onChange={() => setForm({ ...form, target_scope: "selected" })} /> Clienți selectați
            </label>
          </div>
        </div>
        {form.target_scope === "selected" && (
          <div style={{ marginTop: 8, maxHeight: 160, overflowY: "auto", border: "1px solid #E2E8F0", borderRadius: 6, padding: 8 }}>
            {clients.map(c => (
              <label key={c.cui} style={{ display: "block", fontSize: 13, padding: "2px 0" }}>
                <input type="checkbox" checked={form.cuis.includes(c.cui)} onChange={() => toggleCui(c.cui)} /> {c.company_name} ({c.cui})
              </label>
            ))}
          </div>
        )}
        <div style={{ marginTop: 16 }}>
          <button className="btn btn-primary" onClick={createRelease} disabled={busy} data-testid="release-create">
            {busy ? "Se procesează..." : "Creează draft"}
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 22 }} data-testid="release-list">
        <div style={{ fontWeight: 700, color: "var(--navy)", marginBottom: 4 }}>Evidența release-urilor ({releases.length})</div>
        <div style={{ fontSize: 12, color: "#64748B", marginBottom: 12 }}>Cronologic, cele mai noi primele.</div>
        {releases.map(r => (
          <div key={r.id} style={{ padding: "10px 0", borderBottom: "1px solid #E2E8F0" }} data-testid={`release-row-${r.version}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <span style={{ fontWeight: 700 }}>v{r.version}</span>{" "}
                <span className={`badge ${r.status === "published" ? "badge-active" : "badge-draft"}`}>
                  {r.status === "published" ? "Publicat" : "Draft"}
                </span>{" "}
                <span style={{ fontSize: 12, color: "#64748B" }}>
                  {r.status === "published" && r.published_at
                    ? `publicat ${r.published_at.slice(0, 10)}`
                    : `creat ${(r.created_at || "").slice(0, 10)}`}
                  {" · "}{r.target_scope === "all" ? "toți clienții" : "clienți selectați"}
                  {" · "}{r.entry_count} intrări · commit {(r.git_ref || "").slice(0, 7)}
                </span>
                {r.git_subject && <div style={{ fontSize: 12, color: "#94A3B8" }}>{r.git_subject}</div>}
                {r.status === "published" && (
                  <div style={{ fontSize: 12, color: "#64748B" }}>
                    Raportat instalat la: {r.installed_on.length ? r.installed_on.join(", ") : "niciun client încă"}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-secondary" onClick={() => toggleExpand(r.id)}>
                  {expandedReleaseId === r.id ? "Ascunde" : "Vezi intrări"}
                </button>
                {r.status === "published" && (
                  <button className="btn btn-secondary" onClick={() => startRepub(r)} disabled={busy} data-testid={`republish-${r.version}`}>
                    Publică și pentru alt client
                  </button>
                )}
                {r.status === "draft" && (
                  <>
                    <button className="btn btn-primary" onClick={() => { setPublishingId(publishingId === r.id ? null : r.id); setPublishConfirm(""); }} disabled={busy}>Publică</button>
                    <button className="btn btn-secondary" onClick={() => deleteRelease(r.id)} style={{ color: "#B91C1C" }}>Șterge</button>
                  </>
                )}
              </div>
            </div>
            {publishingId === r.id && r.status === "draft" && (
              <div style={{ marginTop: 10, padding: 12, background: "#FEF3C7", borderRadius: 6, fontSize: 13 }} data-testid="publish-confirm">
                Publicarea e <b>ireversibilă</b>: release-ul devine imuabil și apare clienților. Tastează <b>{r.version}</b> pentru a confirma.
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <input className="input" value={publishConfirm} onChange={e => setPublishConfirm(e.target.value)}
                    placeholder={r.version} style={{ maxWidth: 180 }} data-testid="publish-confirm-input" />
                  <button className="btn btn-primary" onClick={() => publishRelease(r)} disabled={busy || publishConfirm.trim() !== r.version}
                    data-testid="publish-confirm-button">{busy ? "Se publică..." : "Confirmă publicarea"}</button>
                  <button className="btn btn-secondary" onClick={() => { setPublishingId(null); setPublishConfirm(""); }}>Anulează</button>
                </div>
              </div>
            )}
            {repub && repub.id === r.id && (
              <div style={{ marginTop: 10, padding: 12, background: "#FEF3C7", borderRadius: 6, fontSize: 13 }} data-testid="republish-form">
                Se creează un release <b>nou</b> cu același cod ca v{r.version} (commit {(r.git_ref || "").slice(0, 7)}) și intrările lui de changelog; v{r.version} rămâne neschimbat.
                <div style={{ display: "flex", gap: 16, marginTop: 8, flexWrap: "wrap" }}>
                  <label>Versiune nouă <input className="input" style={{ maxWidth: 120 }} value={repub.version}
                    onChange={e => setRepub({ ...repub, version: e.target.value })} data-testid="republish-version" /></label>
                  <label><input type="radio" checked={repub.scope === "selected"} onChange={() => setRepub({ ...repub, scope: "selected" })} /> Clienți selectați</label>
                  <label><input type="radio" checked={repub.scope === "all"} onChange={() => setRepub({ ...repub, scope: "all" })} /> Toți clienții</label>
                </div>
                {repub.scope === "selected" && (
                  <div style={{ marginTop: 8, maxHeight: 140, overflowY: "auto", background: "#fff", borderRadius: 6, padding: 8 }}>
                    {(clients || []).map(c => (
                      <label key={c.cui} style={{ display: "block", padding: "2px 0" }}>
                        <input type="checkbox" checked={repub.cuis.includes(c.cui)}
                          onChange={() => setRepub({ ...repub, cuis: repub.cuis.includes(c.cui) ? repub.cuis.filter(x => x !== c.cui) : [...repub.cuis, c.cui] })} /> {c.company_name} ({c.cui})
                      </label>
                    ))}
                  </div>
                )}
                <div style={{ marginTop: 8 }}>Publicarea e <b>ireversibilă</b>. Tastează <b>{repub.version}</b> pentru a confirma.</div>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <input className="input" style={{ maxWidth: 180 }} value={repub.confirm} placeholder={repub.version}
                    onChange={e => setRepub({ ...repub, confirm: e.target.value })} data-testid="republish-confirm-input" />
                  <button className="btn btn-primary" onClick={doRepub}
                    disabled={busy || !repub.version.trim() || repub.confirm.trim() !== repub.version.trim() || (repub.scope === "selected" && !repub.cuis.length)}
                    data-testid="republish-confirm-button">{busy ? "Se publică..." : "Confirmă publicarea"}</button>
                  <button className="btn btn-secondary" onClick={() => setRepub(null)}>Anulează</button>
                </div>
              </div>
            )}
            {expandedReleaseId === r.id && releaseEntries[r.id] && (
              <ul style={{ marginTop: 8, paddingLeft: 20, fontSize: 13, color: "#334155" }}>
                {releaseEntries[r.id].map(e => <li key={e.id}>{e.description}</li>)}
              </ul>
            )}
          </div>
        ))}
        {releases.length === 0 && <div style={{ color: "#94A3B8", fontSize: 13 }}>Niciun release încă.</div>}
      </div>
    </>
  );
}
